import type { StockerDimensions, StockerRobotState } from './stockerTypes';
import {
  DROP_OHT_RELEASE,
  DROP_PORT_REST_END,
  PICKUP_OHT_TAKE,
  PICKUP_PORT_READY,
} from './stockerTransferTiming';

type CraneMotion = {
  carriage: [number, number, number];
  forkZ: number;
  forkVisible: boolean;
  carrying: boolean;
};

type ActiveTransfer = {
  progress: number;
  isPickup: boolean;
};

export function resolveStockerRobotRail(dims: StockerDimensions) {
  const railBottom = dims.loadPortDeckY - dims.frameT * 0.3;
  const railTop = dims.pillarTop - dims.frameT * 1.4;
  const railH = Math.max(0.01, railTop - railBottom);
  return { railBottom, railTop, railH };
}

/** Idle pose — lifter parked at the bottom near the OHT load-port row. */
export function resolveStockerRobotParkState(dims: StockerDimensions): StockerRobotState {
  const { railBottom, railH } = resolveStockerRobotRail(dims);
  const carriageT = 0.04;
  return {
    status: 'idle',
    carriageT,
    carriageX: 0,
    carriageY: railBottom + carriageT * railH,
    forkLocalZ: 0,
    armExtendT: 0,
    forkMode: 'retracted',
    carryingFoup: false,
  };
}

/** Map transfer crane motion → StockerRobot lifter pose (OHT-driven only). */
export function mapTransferMotionToRobotState(
  motion: CraneMotion,
  dims: StockerDimensions,
  width: number,
  active: ActiveTransfer | null,
  aisleZ: number
): StockerRobotState {
  const { railBottom, railH } = resolveStockerRobotRail(dims);
  const forkReach = dims.loadPortZ - dims.lifterAisleZ;
  const shelfReachX = width * 0.28;
  const y = motion.carriage[1];
  const carriageT = Math.min(1, Math.max(0, (y - railBottom) / railH));
  const forkLocalZ = motion.forkVisible ? motion.forkZ - aisleZ : 0;
  const carriageX = motion.carriage[0];

  if (!active) {
    return resolveStockerRobotParkState(dims);
  }

  const p = active.progress;
  let status: StockerRobotState['status'] = 'idle';
  let forkMode: StockerRobotState['forkMode'] = 'retracted';

  if (active.isPickup) {
    if (p < PICKUP_PORT_READY) {
      status = motion.carrying ? 'movingToLoadPort' : 'movingToSlot';
      forkMode = motion.carrying ? 'port' : 'shelf';
    } else if (p < PICKUP_OHT_TAKE) {
      status = 'placing';
      forkMode = 'port';
    } else {
      status = 'idle';
      forkMode = 'retracted';
    }
  } else if (p < DROP_OHT_RELEASE) {
    status = 'idle';
    forkMode = 'retracted';
  } else if (p < DROP_PORT_REST_END) {
    status = 'movingToLoadPort';
    forkMode = 'port';
  } else {
    status = motion.carrying ? 'movingToSlot' : 'placing';
    forkMode = motion.carrying ? 'shelf' : 'port';
    if (motion.carrying) {
      status = 'movingToSlot';
      forkMode = 'shelf';
    } else if (Math.abs(carriageX) > width * 0.08) {
      status = 'placing';
      forkMode = 'shelf';
    } else {
      status = 'picking';
      forkMode = 'port';
    }
  }

  const portExtend = Math.min(
    1,
    Math.abs(motion.forkZ - aisleZ) / Math.max(Math.abs(forkReach), 0.05)
  );
  const shelfExtend = Math.min(1, Math.abs(carriageX) / Math.max(shelfReachX, 0.05));

  let armExtendT = 0;
  if (forkMode === 'port') {
    armExtendT = motion.forkVisible ? portExtend : 0;
  } else if (forkMode === 'shelf') {
    armExtendT = motion.forkVisible ? Math.max(shelfExtend, portExtend * 0.15) : 0;
  }

  return {
    status,
    carriageX,
    carriageY: y,
    carriageT,
    forkLocalZ,
    armExtendT,
    forkMode,
    carryingFoup: motion.carrying,
  };
}
